// Examen d'une pièce : aperçu, valeurs lues avec leur source, saisie ou correction, métadonnées,
// code 2D-DOC et procédure guidée de l'avis d'imposition.
import { useEffect, useState } from "react";
import { ApiError, post, put } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateTime, fileSize, money, today } from "@core/format";
import { Badge, Button, ErrorBox, Fil, Input, KeyValues, Loading, MoneyInput, Notice, Select, SimBadge, Source, StatusChip, Table, Textarea, fieldError, useToast } from "@core/ui";
import { CheckList, SIGNATURE_LABELS, TAX_RESULTS, type Check } from "./shared";

type Field = {
  id: string;
  field: string;
  label: string;
  kind: string;
  sensitive: boolean;
  raw_value: string;
  display: string;
  origin: string;
  origin_label: string;
  page: number | null;
  zone: string | null;
  confidence: string | null;
  note: string;
  created_at: string;
};

type Detail = {
  document: { id: string; category_label: string; filename: string; mime: string; size_bytes: number; version: number; created_at: string; fichier: string };
  schema: { name: string; label: string; kind: string; sensitive: boolean }[];
  champs: Field[];
  historique: Field[];
  lectures: { id: string; available: boolean; provider: string; mode: string; model: string | null; note: string | null; created_at: string }[];
  analyse_fichier: any;
  deux_d_doc: any[];
  verifications_fiscales: any[];
  procedure_fiscale: any;
  constats: Check[];
};

const KIND_HELP: Record<string, string> = {
  amount: "Montant en euros, par exemple 2 345,67",
  date: "Date, par exemple 15/03/2026",
  month: "Mois, par exemple 03/2026",
  year: "Année sur quatre chiffres",
  digits: "Chiffres uniquement",
};

function FieldSource({ f }: { f: Field }) {
  const details = [f.origin_label, ...(f.page ? [`page ${f.page}`] : []), ...(f.zone ? [f.zone] : []), ...(f.confidence ? [`confiance ${f.confidence}`] : [])];
  return <Source doc={f.label} details={details} extrait={f.origin === "lecture_ia"} />;
}

export function PieceDetail({ applicationId, docId, orgId, canWrite }: { applicationId: string; docId: string; orgId?: string; canWrite: boolean }) {
  const path = `/api/v1/applications/${applicationId}/pieces/${docId}`;
  const q = useGet<Detail>(path);
  const invalidate = useInvalidate();
  const toast = useToast();
  const landlord = !!orgId;
  const base = orgId ? `/api/v1/orgs/${orgId}/applications/${applicationId}` : "";
  const refresh = () => invalidate(`/api/v1/applications/${applicationId}`, ...(orgId ? [`/api/v1/orgs/${orgId}/applications/${applicationId}`] : []));

  const [values, setValues] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [tax, setTax] = useState({ resultat: "conforme_dernier_avis", constate_le: today(), note: "", rfr: null as number | null });

  useEffect(() => {
    if (!q.data) return;
    const v: Record<string, string> = {};
    for (const s of q.data.schema) v[s.name] = q.data.champs.find((c) => c.field === s.name)?.raw_value ?? "";
    setValues(v);
  }, [q.data]);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data!;
  const doc = d.document;
  const lastRead = d.lectures[0];
  const isImage = doc.mime.startsWith("image/") && doc.mime !== "image/heic";

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      toast(ok);
      refresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  const changed = Object.fromEntries(Object.entries(values).filter(([k, v]) => (d.champs.find((c) => c.field === k)?.raw_value ?? "") !== v));
  const report = d.analyse_fichier;

  return (
    <div className="stack">
      <div className="row-between">
        <div>
          <p className="small muted">
            {doc.category_label} · version {doc.version} · déposée le {date(doc.created_at)} · {fileSize(doc.size_bytes)}
          </p>
        </div>
        <a className="bv-btn bv-btn-secondaire bv-btn-petit" href={doc.fichier} target="_blank" rel="noreferrer">
          Ouvrir la pièce
        </a>
      </div>
      {isImage ? (
        <img className="controle-apercu-image" src={doc.fichier} alt={`Aperçu : ${doc.category_label}`} />
      ) : doc.mime === "application/pdf" ? (
        <object className="controle-apercu" data={doc.fichier} type="application/pdf" aria-label={`Aperçu : ${doc.category_label}`}>
          <p>
            Aperçu indisponible dans ce navigateur. <a href={doc.fichier}>Ouvrir la pièce</a>
          </p>
        </object>
      ) : null}

      <ErrorBox error={error instanceof ApiError && error.fields.length ? null : error} />

      <section className="stack-sm">
        <div className="row-between">
          <h3>Valeurs lues et leur source</h3>
          {landlord && canWrite && d.schema.length ? (
            <Button variant="secondaire" small busy={busy === "lecture"} onClick={() => run("lecture", () => post(`${base}/pieces/${docId}/lecture`), "Lecture terminée.")}>
              Lire la pièce
            </Button>
          ) : null}
        </div>
        {lastRead ? (
          lastRead.available ? (
            <p className="small muted">
              Lecture automatique du {dateTime(lastRead.created_at)} ({lastRead.provider}
              {lastRead.model ? `, ${lastRead.model}` : ""}) <SimBadge mode={lastRead.mode} />. Chaque valeur peut être corrigée avant calcul.
            </p>
          ) : (
            <Notice tone="simulateur" title="Lecture automatique non disponible">
              {lastRead.note ?? "Les valeurs sont à saisir ou confirmer manuellement."} <SimBadge mode={lastRead.mode} />
            </Notice>
          )
        ) : null}
        {d.champs.length ? (
          <Table caption="Valeurs de la pièce">
            <thead>
              <tr>
                <th>Champ</th>
                <th>Valeur</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {d.champs.map((f) => (
                <tr key={f.id}>
                  <td>{f.label}</td>
                  <td className="controle-valeur">{f.display}</td>
                  <td>
                    <FieldSource f={f} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="muted">Aucune valeur lue ou saisie : aucun montant n’est supposé pour cette pièce.</p>
        )}
      </section>

      {canWrite && d.schema.length ? (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            run("champs", () => put(`${path}/champs`, { champs: changed, note }), "Valeurs enregistrées. Les contrôles concernés sont refaits.");
          }}
        >
          <h3>{d.champs.length ? "Corriger ou compléter les valeurs" : "Saisir les valeurs"}</h3>
          <p className="small muted">La valeur initiale n’est jamais effacée : chaque correction est datée et conservée dans l’historique.</p>
          <div className="form-row">
            {d.schema.map((s) => (
              <Input
                key={s.name}
                label={s.label}
                value={values[s.name] ?? ""}
                help={KIND_HELP[s.kind]}
                error={fieldError(error, s.name)}
                onChange={(e) => setValues({ ...values, [s.name]: e.target.value })}
              />
            ))}
          </div>
          <Textarea label="Motif de la correction (facultatif)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          <div>
            <Button type="submit" busy={busy === "champs"} disabled={!Object.keys(changed).length}>
              Enregistrer les valeurs
            </Button>
          </div>
        </form>
      ) : null}

      {d.historique.length ? (
        <details className="controle-details">
          <summary>Historique des valeurs ({d.historique.length})</summary>
          <Table caption="Valeurs remplacées">
            <thead>
              <tr>
                <th>Champ</th>
                <th>Valeur</th>
                <th>Source</th>
                <th>Enregistrée le</th>
              </tr>
            </thead>
            <tbody>
              {d.historique.map((f) => (
                <tr key={f.id}>
                  <td>{f.label}</td>
                  <td className="controle-valeur">{f.display}</td>
                  <td>{f.origin_label}</td>
                  <td>{dateTime(f.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </details>
      ) : null}

      {d.constats.length ? (
        <section className="stack-sm">
          <h3>Constats sur cette pièce</h3>
          <CheckList checks={d.constats} showPerson={false} />
        </section>
      ) : null}

      {report ? (
        <details className="controle-details">
          <summary>Métadonnées et structure du fichier</summary>
          <div className="stack-sm">
            <p className="small muted">
              Faits observés dans le fichier. Une trace d’outil d’édition appelle un examen, jamais une conclusion ; son absence ne prouve rien.
            </p>
            <KeyValues
              items={[
                ["Type", report.kind === "pdf" ? `PDF ${report.pdf_version ?? ""}` : report.kind === "image" ? `Image ${report.width ?? "?"} × ${report.height ?? "?"} pixels` : "Autre"],
                ["Pages", report.pages ?? "—"],
                ["Révisions", report.revisions ?? "—"],
                ...Object.entries(report.info ?? {}).map(([k, v]) => [k, String(v)] as [string, string]),
                ...Object.entries(report.xmp ?? {}).map(([k, v]) => [`XMP ${k}`, String(v)] as [string, string]),
                ...Object.entries(report.exif ?? {}).map(([k, v]) => [`EXIF ${k}`, String(v)] as [string, string]),
                ["Polices", (report.fonts ?? []).join(", ") || "—"],
                ["Images intégrées", String((report.images ?? []).length)],
              ]}
            />
            {(report.traces ?? []).length ? (
              <ul className="list-plain stack-sm">
                {report.traces.map((t: any, i: number) => (
                  <li key={i}>
                    <Source doc={t.tool} details={[t.field, t.scope === "objet" ? `objet intégré ${t.object}` : "fichier entier"]} />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </details>
      ) : null}

      {landlord || d.deux_d_doc.length ? (
        <section className="stack-sm">
          <h3>Code 2D-DOC</h3>
          {d.deux_d_doc.length ? (
            d.deux_d_doc.slice(0, 1).map((r) => {
              const [status, label] = SIGNATURE_LABELS[r.signature_status] ?? ["info", r.signature_status];
              return (
                <div key={r.id} className="stack-sm">
                  <p className="row" style={{ flexWrap: "wrap" }}>
                    {r.parse_status === "lu" ? <StatusChip status={status} label={label} /> : <StatusChip status="non_concluant" label={r.parse_status === "format_non_pris_en_charge" ? "Format non pris en charge" : "Code illisible"} />}
                    <SimBadge mode={r.trust_mode ?? "simulateur"} />
                    <span className="small muted">
                      {r.source === "saisie_manuelle" ? "Contenu collé" : "Lu dans l’image"} le {dateTime(r.created_at)}
                    </span>
                  </p>
                  {r.parse_status === "lu" ? (
                    <KeyValues
                      items={[
                        ["Autorité et certificat", `${r.header.ca_id} · ${r.header.cert_id}`],
                        ["Type de document", r.header.doc_type_label],
                        ["Émis le", date(r.header.issued_on)],
                        ["Signé le", date(r.header.signed_on)],
                        ...r.fields.map((f: any) => [f.label, f.value] as [string, string]),
                      ]}
                    />
                  ) : null}
                  <p className="small muted">Dictionnaire des champs à revérifier contre la spécification ANTS en vigueur. La signature ne couvre que les données du code.</p>
                </div>
              );
            })
          ) : (
            <p className="muted">Aucun code lu pour cette pièce.</p>
          )}
          {landlord && canWrite ? (
            <form
              className="stack-sm"
              onSubmit={(e) => {
                e.preventDefault();
                run("code", () => post(`${base}/pieces/${docId}/deux-d-doc`, { contenu: code }), "Code analysé.");
              }}
            >
              <Textarea
                label="Contenu décodé du code (facultatif)"
                help="Si le code n’a pas été détecté, collez le texte donné par une application de lecture 2D-DOC. Les séparateurs peuvent s’écrire <GS>, <RS> et <US>."
                value={code}
                error={fieldError(error, "contenu")}
                onChange={(e) => setCode(e.target.value)}
                rows={3}
              />
              <div>
                <Button variant="secondaire" type="submit" busy={busy === "code"} disabled={!code.trim()}>
                  Analyser le contenu
                </Button>
              </div>
            </form>
          ) : null}
        </section>
      ) : null}

      {d.procedure_fiscale ? (
        <section className="stack-sm">
          <h3>Contrôle de l’avis auprès du service officiel</h3>
          <Fil steps={d.procedure_fiscale.etapes.map((t: string, i: number) => ({ title: t, state: i === 0 ? "actuel" : "a-venir" }))} />
          <KeyValues
            items={[
              ["Numéro fiscal (à saisir sur le service)", <span className="mono">{d.procedure_fiscale.valeurs.numero_fiscal ?? "non lu"}</span>],
              ["Référence de l’avis", <span className="mono">{d.procedure_fiscale.valeurs.reference_avis ?? "non lue"}</span>],
            ]}
          />
          <p className="row">
            <a className="bv-btn bv-btn-secondaire bv-btn-petit" href={d.procedure_fiscale.url} target="_blank" rel="noreferrer">
              Ouvrir le service officiel
            </a>
            <a className="small" href={d.procedure_fiscale.presentation} target="_blank" rel="noreferrer">
              Portée de ce contrôle
            </a>
          </p>
          <p className="small muted">Bailivo n’accède pas à ce service : vous saisissez vous-même les références, puis vous enregistrez le résultat constaté. Le revenu fiscal de référence porte sur l’année précédente et sur tout le foyer.</p>
          {canWrite ? (
            <form
              className="stack-sm"
              onSubmit={(e) => {
                e.preventDefault();
                run(
                  "fiscal",
                  () => post(`${base}/pieces/${docId}/verification-fiscale`, { resultat: tax.resultat, constate_le: tax.constate_le, note: tax.note, rfr_constate_cents: tax.rfr, reference: d.procedure_fiscale.valeurs.reference_avis ?? undefined }),
                  "Résultat enregistré.",
                );
              }}
            >
              <div className="form-row">
                <Select label="Résultat affiché par le service" value={tax.resultat} options={TAX_RESULTS} error={fieldError(error, "resultat")} onChange={(e) => setTax({ ...tax, resultat: e.target.value })} />
                <Input label="Constaté le" type="date" value={tax.constate_le} max={today()} error={fieldError(error, "constate_le")} onChange={(e) => setTax({ ...tax, constate_le: e.target.value })} />
                <MoneyInput label="Revenu fiscal de référence affiché (facultatif)" cents={tax.rfr} onChange={(c) => setTax({ ...tax, rfr: c })} />
              </div>
              <Input label="Note (facultatif)" value={tax.note} onChange={(e) => setTax({ ...tax, note: e.target.value })} />
              <div>
                <Button type="submit" busy={busy === "fiscal"}>
                  Enregistrer le résultat constaté
                </Button>
              </div>
            </form>
          ) : null}
          {d.verifications_fiscales.length ? (
            <ul className="list-plain stack-sm">
              {d.verifications_fiscales.map((v) => (
                <li key={v.id} className="small">
                  <Badge tone={v.result === "conforme_dernier_avis" ? "succes" : v.result === "service_indisponible" ? "neutre" : "attention"}>{TAX_RESULTS.find((t) => t.value === v.result)?.label ?? v.result}</Badge> constaté le {date(v.observed_on)}
                  {v.observed_rfr_cents !== null ? ` · revenu fiscal affiché ${money(v.observed_rfr_cents)}` : ""}
                  {v.reference_masked ? ` · référence ${v.reference_masked}` : ""}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
